// Moved from `src/features/dictationLogs/hooks/queries/useQueryDictationAppCategories.ts`
// (now a delegate) at D1 slice 1, ARCFE-1067.
//
// THE HOOK MOVED WHOLE; THE TWO CACHE READERS MOVED WITH A PARAMETER. That is
// the one signature change in this carve and it is deliberate. `getAppToneMap`
// and `getDictationFallbackTone` are module-level SYNCHRONOUS cache reads —
// neither an invalidator nor inside React — so the census `detail`'s advice
// ("rewrite to `useQueryClient()` in-hook") does not apply to them, as the
// preferences family record already recorded before this carve started. Their
// heal is the other one that record names: a port. They now take the
// `QueryClient` explicitly, and the app delegate binds the `@/utils/reactQueryClient`
// singleton so BOTH OLD CALL SIGNATURES STAY ZERO-ARITY. Their only production
// consumer, `src/tauri/dictation/config.ts`, is untouched.
//
// The alternative — leaving both readers app-side — was rejected because they
// are `dictation/preferences` domain logic reading a `dictation/preferences`
// cache identity, and parking them app-side to dodge one parameter would leave
// the family's own tone-resolution rules outside the package that owns them.
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import {
  DICTATION_APP_CATEGORIES_KEY,
  type DictationAppCategoriesResponse,
  type DictationAppCategory,
  type DictationTone,
} from '../../contracts';
import { getDictationAppCategories } from '../api/preferences';
import { buildAppToneMap } from '../buildAppToneMap';
import { normalizeTone } from '../normalizeTone';

export function useQueryDictationAppCategories(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [DICTATION_APP_CATEGORIES_KEY],
    queryFn: getDictationAppCategories,
    enabled: options?.enabled,
    select: (data): DictationAppCategory[] =>
      data.categories.map((category) => ({
        ...category,
        default_tone: normalizeTone(category.default_tone),
        tone: normalizeTone(category.tone),
      })),
  });
}

/** App-identifier → tone map from the cached categories; undefined until warmed. */
export function getAppToneMap(
  queryClient: QueryClient
): Record<string, DictationTone> | undefined {
  const data = queryClient.getQueryData<DictationAppCategoriesResponse>([
    DICTATION_APP_CATEGORIES_KEY,
  ]);
  if (!data) return undefined;
  const map = buildAppToneMap(data.categories);
  return Object.keys(map).length > 0 ? map : undefined;
}

/** Documentation-category tone for undetected/unmapped apps; undefined until warmed. */
export function getDictationFallbackTone(
  queryClient: QueryClient
): DictationTone | undefined {
  const data = queryClient.getQueryData<DictationAppCategoriesResponse>([
    DICTATION_APP_CATEGORIES_KEY,
  ]);
  const documentation = data?.categories.find(
    (category) =>
      category.id === 'documentation' || category.name.toLowerCase() === 'documentation'
  );
  return documentation ? normalizeTone(documentation.tone) : undefined;
}
